import { MarkerType, type Size } from '@ikegps/core-api';
import type { Point } from '@ikegps/core-api';

import { IkeId } from '../../id';
import {
	CollectionModelDrawableMarker,
	CollectionModelFieldHeight,
	CollectionModelFieldVerticalSeparation,
} from '../../model';
import { IkeDraw } from '../draw';
import { IkeDrawTool } from '../draw.state';
import { LabelSVGDraw } from '../drawable/label.draw.svg';
import { IkeDrawLabel } from '../drawable/util.draw.svg';
import { movePoint } from '../util';

export interface MarkerCreateEvent {
	drawableId?: string;
	center: Point;
	$event: MouseEvent;
}

export const LABEL_DELTA = { x: 0, y: 50 } as const;
export function getLabelPoint(uiScale: number, resolution: Size, marker: Point): Point {
	const labelPoint = { x: resolution.width * 0.75, y: marker.y };

	return movePoint(labelPoint, LABEL_DELTA, resolution, {
		width: LabelSVGDraw.Size.width * uiScale,
		height: LabelSVGDraw.height(1) * uiScale,
	});
}

function createMarkerAt(dc: IkeDraw, markerPoint: Point, type = MarkerType.NONE) {
	const labelPoint = getLabelPoint(dc.state.scale, dc.capture.resolution, markerPoint);
	const { marker } = CollectionModelDrawableMarker.createWithLabel(
		dc.drawState,
		markerPoint,
		labelPoint,
		IkeId.generate(),
		type
	);

	dc.state.setSelected(marker.id, IkeDrawLabel.LABEL);

	return marker.id;
}

export function drawableMarkerCreateBinding(dc: IkeDraw, evt: MarkerCreateEvent) {
	if (dc.state.tool === IkeDrawTool.BASE) {
		const drawableId = createMarkerAt(dc, evt.center, MarkerType.BASE);
		const drawable = dc.drawState.getDrawable<CollectionModelDrawableMarker>(drawableId);

		// Base Marker's label are shown hard against the left edge
		drawable.label.move(0, drawable.label.y);
		dc.state.setTool(null);
		dc.state.setCrosshair(false);
		return;
	}

	if (evt.drawableId == null) {
		evt.drawableId = createMarkerAt(dc, evt.center);
	}

	const linkFieldIds = dc.state.linkFieldIds;
	if (linkFieldIds.length > 0) {
		const drawable = dc.drawState.getDrawable<CollectionModelDrawableMarker>(evt.drawableId);
		if (!CollectionModelDrawableMarker.isNone(drawable)) {
			return;
		}

		for (const linkFieldId of linkFieldIds) {
			const field = dc.collection.findField<CollectionModelFieldHeight | CollectionModelFieldVerticalSeparation>(
				linkFieldId
			);
			if (!field) {
				throw new Error(`Unable to find field: ${linkFieldId}`);
			}
			if (field instanceof CollectionModelFieldVerticalSeparation) {
				field.Mutator.drawable(drawable);
				drawable.label.linkVerticalSeparation(field);
			} else {
				field.Mutator.drawable(drawable);
			}
		}

		dc.state.setLinkFields(null);
		dc.state.setSelected(drawable.id, IkeDrawLabel.LABEL);

		drawable.approveInsightFlag();
	}

	if (evt.$event != null) {
		evt.$event.preventDefault();
	}
}
